import { type GetReceiptsQueryParams } from './aliases';
import { mapPresentationResponse } from './presentationAdapter';
import { mapPresentationReceiptDetail } from './presentationDetailAdapter';
import {
  type FetchReceiptsArgs,
  type GetReceiptsResponse,
  type Receipt,
  type Role,
  SoldItemsShippingStatus,
} from './types';

import {
  RECEIPT_BY_ID_ENDPOINT,
  RECEIPTS_ENDPOINT,
} from '@/constants/endpoints';
import { typedHttp } from '@/modules/http';
import { replacePathParams } from '@/modules/http/replacePathParams';
import { type AxiosCompatibleResponse } from '@/modules/http/types';

const RECEIPT_COUNT_PER_PAGE = 12;

/** Web sold-items filter tab → presentation `status` CSV (calculated response statuses). */
const STATUS_FILTER: Record<SoldItemsShippingStatus, string | undefined> = {
  [SoldItemsShippingStatus.All]: undefined,
  [SoldItemsShippingStatus.ToShip]: 'shipping_pending',
  [SoldItemsShippingStatus.InTransit]: 'shipped',
  [SoldItemsShippingStatus.Cancelled]: 'cancelled',
  [SoldItemsShippingStatus.Completed]:
    'delivered,delivered_pending_feedback,refunded',
};

export async function getReceipts({
  shippingStatus,
  cursor,
  role,
  limit = RECEIPT_COUNT_PER_PAGE,
  sku,
  userId,
}: FetchReceiptsArgs): Promise<AxiosCompatibleResponse<GetReceiptsResponse>> {
  const status = shippingStatus ? STATUS_FILTER[shippingStatus] : undefined;
  const params: GetReceiptsQueryParams = {
    role,
    // The contract types `limit` as a string (it is a query param).
    limit: String(limit),
    ...(cursor ? { after: cursor } : {}),
    ...(status ? { status } : {}),
    ...(sku ? { sku } : {}),
    ...(userId ? { other_user: String(userId) } : {}),
  };
  const response = await typedHttp.get(RECEIPTS_ENDPOINT, {
    params,
    withAuth: true,
  });
  return { ...response, data: mapPresentationResponse(response.data) };
}

type GetReceiptArgs = {
  purchaseId: number;
  country: string;
  language: string;
  role?: Role;
};

export async function getReceipt({
  purchaseId,
}: GetReceiptArgs): Promise<AxiosCompatibleResponse<Receipt.Receipt>> {
  const response = await typedHttp.get(
    replacePathParams(RECEIPT_BY_ID_ENDPOINT, {
      receiptId: purchaseId.toString(),
    }),
    { withAuth: true }
  );
  return { ...response, data: mapPresentationReceiptDetail(response.data) };
}
